import { getCurrencySymbol, DatePipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, OnChanges, SimpleChanges, LOCALE_ID, input, output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ClientService, PaymentGatewayMasterService } from '@fitness-force/api';
import type { PaymentGatewatMaster, PaymentGatewatMasterList, PaymentMethod } from '@fitness-force/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RadioButtonModule } from 'primeng/radiobutton';
import { BehaviorSubject, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MessageModule } from 'primeng/message';
import { TooltipModule } from 'primeng/tooltip';
import { getPaymentMethodDisplayText, getPaymentLevel1Text } from '@fitness-force/shared-utils';
import { PaymentGatewaySupported, Status } from '@fitness-force/enum';

@Component({
  selector: 'saved-payment-methods-popup',
  templateUrl: './saved-payment-methods-popup.component.html',
  styleUrls: ['./saved-payment-methods-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    DialogModule,
    ButtonModule,
    RadioButtonModule,
    ProgressSpinnerModule,
    TranslateModule,
    MessageModule,
    TooltipModule,
    DatePipe,
    NgClass
]
})
export class SavedPaymentMethodsPopupComponent implements OnInit, OnChanges {
  private readonly clientService = inject(ClientService);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);
  private readonly paymentGatewayMasterService = inject(PaymentGatewayMasterService);
  private readonly localeId = inject(LOCALE_ID);

  readonly visible = input<boolean>(false);
  readonly clientId = input<number>(0);
  readonly tenantId = input<number>(0);
  readonly currencyCode = input<string>('USD');
  
  readonly hideDialog = output<void>();
  readonly paymentMethodSelected = output<number>();

  paymentMethods$ = new BehaviorSubject<PaymentMethod[]>([]);
  configuredPaymentGateways: PaymentGatewatMaster[] = [];
  loading$ = new BehaviorSubject<boolean>(false);
  selectedPaymentMethodId: number | null = null;
  currencySymbol$ = new BehaviorSubject<string>('');
  locale: string = navigator.language;

  constructor() {
    if (this.locale == 'ar') this.locale = "ar-SA";
  }

  ngOnInit(): void {
    this.currencySymbol$.next(
      getCurrencySymbol(this.currencyCode(), 'narrow', this.localeId)
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible() && this.clientId() > 0) {
      this.loadPaymentMethods();
    }
  }

  loadPaymentMethods(): void {
    this.loading$.next(true);
    this.selectedPaymentMethodId = null;

    forkJoin({
      paymentMethods: this.clientService.getClientPaymentMethods(this.clientId()).pipe(
        catchError(() => of([] as PaymentMethod[]))
      ),
      gateways: this.paymentGatewayMasterService.getPaymentGatewayMasters(this.tenantId()).pipe(
        catchError(() => of({ items: [] } as PaymentGatewatMasterList))
      ),
    }).subscribe({
      next: ({ paymentMethods, gateways }) => {
        this.paymentMethods$.next(paymentMethods || []);
        this.configuredPaymentGateways = gateways.items
          ? gateways.items.filter(pg => pg.status.toString() === Status[Status.Active].toString())
          : [];
        this.loading$.next(false);
        this.cd.detectChanges();
      },
      error: () => {
        this.paymentMethods$.next([]);
        this.configuredPaymentGateways = [];
        this.loading$.next(false);
        this.cd.detectChanges();
      },
    });
  }

  isPaymentMethodEnabled(paymentMethod: PaymentMethod): boolean {
    return paymentMethod?.is_authorized_card_on_file === true;
  }

  isDifferentPaymentGateway(paymentMethod: PaymentMethod): boolean {
    const methodGateway = paymentMethod?.payment_gateway_enum_id;
    if (methodGateway === undefined || methodGateway === null) {
      return false;
    }
    if (this.configuredPaymentGateways.length === 0) {
      return true;
    }
    return !this.configuredPaymentGateways.some(pg =>
      this.isSamePaymentGateway(pg.payment_gateway, methodGateway)
    );
  }

  isPaymentMethodSelectable(paymentMethod: PaymentMethod): boolean {
    return this.isPaymentMethodEnabled(paymentMethod) && !this.isDifferentPaymentGateway(paymentMethod);
  }

  private isSamePaymentGateway(
    configuredGateway: PaymentGatewaySupported,
    methodGateway: PaymentGatewaySupported
  ): boolean {
    return (
      configuredGateway === methodGateway ||
      configuredGateway?.toString() === methodGateway?.toString() ||
      PaymentGatewaySupported[configuredGateway]?.toString() === methodGateway?.toString() ||
      configuredGateway?.toString() === PaymentGatewaySupported[methodGateway as PaymentGatewaySupported]
    );
  }

  getPaymentGatewayDisplayName(gatewayId: PaymentGatewaySupported): string {
    const gatewayName = PaymentGatewaySupported[gatewayId as PaymentGatewaySupported];
    return typeof gatewayName === 'string' ? gatewayName : String(gatewayId);
  }

  getPaymentMethodDisplayText(paymentMethod: PaymentMethod): string {
    return getPaymentMethodDisplayText(paymentMethod);
  }

  getPaymentLevel1Text(paymentMethod: PaymentMethod): string {
    return getPaymentLevel1Text(paymentMethod);
  }

  getPaymentMethodIcon(paymentMethod: PaymentMethod): string {
    // Check for Apple Pay or Google Pay first
    if (paymentMethod.is_apple_pay_or_google_pay) {
      return 'pi pi-mobile';
    }

    // Check by level1 (payment mode type)
    const level1Str = String(paymentMethod.level1).toLowerCase();
    const level1Num = Number(paymentMethod.level1);

    // Card payment methods
    if (level1Num === 2 || level1Str === 'card') {
      return 'pi pi-credit-card';
    }
    
    // UPI payment methods
    if (level1Num === 4 || level1Str === 'upi') {
      return 'pi pi-qrcode';
    }
    
    // NetBanking payment methods
    if (level1Num === 3 || level1Str === 'netbanking') {
      return 'pi pi-building';
    }
    
    // Wallet payment methods
    if (level1Num === 5 || level1Str === 'wallet') {
      return 'pi pi-wallet';
    }
    
    // ACH payment methods
    if (level1Num === 6 || level1Str === 'ach') {
      return 'pi pi-building';
    }
    
    // Cheque payment methods
    if (level1Num === 7 || level1Str === 'cheque') {
      return 'pi pi-file';
    }
    
    // Emandate payment methods
    if (level1Num === 9 || level1Str === 'emandate') {
      return 'pi pi-sync';
    }
    
    // Default icon
    return 'pi pi-wallet';
  }

  getPaymentMethodIconColor(paymentMethod: PaymentMethod): string {
    if (!this.isPaymentMethodSelectable(paymentMethod)) {
      return 'text-400'; // Disabled color
    }

    // Color based on payment type
    const level1Str = String(paymentMethod.level1).toLowerCase();
    const level1Num = Number(paymentMethod.level1);

    if (level1Num === 2 || level1Str === 'card') {
      return 'text-blue-500';
    }
    if (level1Num === 4 || level1Str === 'upi') {
      return 'text-purple-500';
    }
    if (level1Num === 3 || level1Str === 'netbanking') {
      return 'text-green-500';
    }
    if (level1Num === 5 || level1Str === 'wallet') {
      return 'text-orange-500';
    }
    
    return 'text-primary';
  }

  getDisabledMessage(): string {
    return this.translate.instant('POS.MEMBERSHIP_ADDON.PAYMENT_METHOD_DISABLED_MESSAGE');
  }

  getDifferentGatewayMessage(paymentMethod: PaymentMethod): string {
    return this.translate.instant('POS.MEMBERSHIP_ADDON.DIFFERENT_PAYMENT_GATEWAY_MESSAGE', {
      gatewayName: this.getPaymentGatewayDisplayName(paymentMethod.payment_gateway_enum_id),
    });
  }

  getDifferentGatewayTooltip(paymentMethod: PaymentMethod): string {
    return this.translate.instant('POS.MEMBERSHIP_ADDON.DIFFERENT_PAYMENT_GATEWAY_TOOLTIP', {
      gatewayName: this.getPaymentGatewayDisplayName(paymentMethod.payment_gateway_enum_id),
    });
  }

  getPaymentMethodDisabledTooltip(paymentMethod: PaymentMethod): string {
    if (this.isDifferentPaymentGateway(paymentMethod)) {
      return this.getDifferentGatewayTooltip(paymentMethod);
    }
    return this.getDisabledMessage();
  }

  getPaymentSuitabilityText(paymentMethod: PaymentMethod): string {
    const supportsImmediate = paymentMethod.supports_immediate_payment === true;
    const supportsRecurring = paymentMethod.supports_recurring_payment === true;

    if (supportsImmediate && supportsRecurring) {
      return this.translate.instant('POS.MEMBERSHIP_ADDON.SUITABLE_FOR_BOTH');
    } else if (supportsRecurring && !supportsImmediate) {
      return this.translate.instant('POS.MEMBERSHIP_ADDON.SUITABLE_FOR_RECUR');
    } else if (supportsImmediate && !supportsRecurring) {
      return this.translate.instant('POS.MEMBERSHIP_ADDON.ONLY_FOR_INITIAL');
    }
    
    return '';
  }

  showSuitabilityText(paymentMethod: PaymentMethod): boolean {
    return paymentMethod.supports_immediate_payment !== undefined || 
           paymentMethod.supports_recurring_payment !== undefined;
  }

  onPaymentMethodSelect(paymentMethodId: number): void {
    const paymentMethod = this.paymentMethods$.value.find(pm => pm.id === paymentMethodId);
    if (paymentMethod && this.isPaymentMethodSelectable(paymentMethod)) {
      this.selectedPaymentMethodId = paymentMethodId;
    }
  }

  onConfirm(): void {
    if (this.selectedPaymentMethodId) {
      this.paymentMethodSelected.emit(this.selectedPaymentMethodId);
      this.onClose();
    }
  }

  onClose(): void {
    this.selectedPaymentMethodId = null;
    this.hideDialog.emit();
  }

  get hasPaymentMethods(): boolean {
    return this.paymentMethods$.value.length > 0;
  }

  get hasEnabledPaymentMethods(): boolean {
    return this.paymentMethods$.value.some(pm => this.isPaymentMethodSelectable(pm));
  }

  get isConfirmDisabled(): boolean {
    return !this.selectedPaymentMethodId;
  }

  closeDialog() {
    this.hideDialog.emit();
  }
}

